import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { InputField } from '@/components/InputField';
import { Club } from '@/model';
import TeamFilterSelect from '@/components/TeamFilterSelect';
import { IndicationMapFilters } from '../../types';

export interface IndicationMapFilterBoxProps {
  clubs: Club[];
  filtersForm: UseFormReturn<IndicationMapFilters>;
  forceRefetch: () => void;
}

export const IndicationMapFilterBox = ({ clubs, filtersForm }: IndicationMapFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_indications_map.components.location_filter_box.location_filter_filter_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 5, xl: 6 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='100%' />

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_indications_map.components.location_filter_box.location_filter_title_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('title'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_indications_map.components.location_filter_box.location_filter_city_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('city'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_indications_map.components.location_filter_box.location_filter_start_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_indications_map.components.location_filter_box.location_filter_end_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_indications_map.components.location_filter_box.location_filter_clear_filters_button')}
        </Button>
      </Flex>
    </Box>
  );
};
